/**
 * This modules handles dragging of calls and queue members
 */
import globalEvents from '@/utilities/GlobalEvents'
import { useSettingsStore } from '@/stores/Settings'
import { useEmployeesStore } from '@/stores/Employees'
import SipClient from '@/utilities/SipClient'
import _i18n from '@/utilities/I18n'
import logger from '@/utilities/Logger'
import type Employee from '@/stores/Employee'
import type { PbxAppEmployeesExtensionCall, PbxAppEmployeesExtensionCallWithDate, PbxAppQueuesAllItem, PbxAppQueuesCaller, PbxAppQueuesMember } from '@/TcxApi/PbxApp'
import { PbxCallState } from '@/TcxApi/PbxCommon'
import { useApiStore } from '@/stores/Api'
import type { SipClientTcxState } from './SipClient.types'
import type { PbxQueueMemberJoinResponse, PbxQueueMemberJoinRequest } from '@/TcxApi/PbxQueue'
import type { ErrorResponseDetail } from '@/TcxApi/ErrorResponse'
// import type { AxiosInstance } from 'axios'
import { sleep } from './Tools'

let ss: ReturnType<typeof useSettingsStore>
let employees: ReturnType<typeof useEmployeesStore>
const i18n = _i18n.global
let api: ReturnType<typeof useApiStore>
let sipClient: SipClient

export interface QueueEnhancedCall {
	call: PbxAppQueuesCaller
	queueId: string
}

export interface QueueEnhancedMember {
	member: PbxAppQueuesMember
	queueId: string
}

/**
 * Types of items that can be dragged
 */
export enum CallDragItemType {
	/** A call from MyCalls, dragItem is a **PbxAppEmployeesExtensionCall** */
	MyCallsCall = 'MyCallsCall',
	/** An employee from Extensions list, dragItem is an **Employee** */
	Employee = 'Employee',
	/** A queue call from QueueCall, dragItem is a **QueueEnhancedCall** */
	QueueCall = 'QueueCall',
	/** A queue member from QueueMember, dragItem is a **QueueEnhancedMember** */
	QueueMember = 'QueueMember'
}

/**
 * Items that can be dragged (their data tyype)
 */
export type CallDragItem = Employee | PbxAppEmployeesExtensionCallWithDate | QueueEnhancedCall | QueueEnhancedMember

/**
 * Types of targets that can be dropped on
 */
export enum CallDragTargetType {
	/** An Employee from Extensions list, dragTarget is an Employee */
	Employee = 'Employee',
	/** MyCalls box, dragTarget is Employee */
	MyCalls = 'MyCalls',
	/** Queue calls box, dragTarget is PbxAppQueuesAllItem (queue) */
	QueueCallers = 'QueueCallers',
	/** Queue members box, dragTarget is PbxAppQueueAllItem (queue) */
	QueueMembers = 'QueueMembers'
}

/**
 * Targets that can be dropped on (their data type)
 * Employee accepyts: MyCallsCall, Employee, QueueCall
 * PbxAppQueuesAllItem (Queue) accepts: MyCallsCall, QueueCall, Employee, QueueMember (because it handles both QueueCaller and QueueMembers)
 */
export type CallDragTarget = Employee | PbxAppQueuesAllItem

export default class CallDrag {
	static #instance: CallDrag // NOSONAR

	/** Type of item that shall be dragged */
	dragItemType?: CallDragItemType

	/** The dragged item */
	dragItem?: CallDragItem

	/** The target the dragged item was dropped on */
	dragTarget?: CallDragTarget

	/** The type of item the dragged item was dropped on */
	dragTargetType?: CallDragTargetType

	/**
	 * Initialize
	 */
	constructor() {
		this.dragItem = undefined
		this.dragItemType = undefined
		this.dragTarget = undefined
		this.dragTargetType = undefined
		sipClient = SipClient.instance
		ss = useSettingsStore()
		employees = useEmployeesStore()
		api = useApiStore()
	}

	/** Get the CallDrag singleton instance */
	static get instance() {
		CallDrag.create()
		return this.#instance
	}

	/** Create the singleton instance */
	static create() {
		if (!this.#instance) {
			this.#instance = new CallDrag()
		}
	}

	/** Invoked after a drop has been made, to perform the action that the drop requires */
	dragDropped() {
		// console.log(`Call of type ${this.dragItemType} with id ${this.dragItem._id} was dropped on type ${this.dragTargetType} with id ${this.dragTarget._id}`);

		// Types: MyCalls, MyCallsCall, Employee, QueueCallers, QueueMembers, QueueCall, QueueMember
		// MyCalls accepts: Employee, QueueCall
		// MyCallsCall accepts: none - never does
		// Employee accepts: MyCallsCall, Employee (for picking up ringing/active call), QueueCall
		// QueueCallers accepts: MyCallsCall,
		// QueueMembers accepts: Employee,
		// QueueCall accepts: none - never does
		// QueueMember accepts: none - never does

		switch (this.dragItemType) {
			case 'MyCallsCall': {
				switch (this.dragTargetType) {
					case 'Employee': this.myCall2Employee(); break
					case 'QueueCallers': this.myCall2Queue(); break
				}
				break
			}

			case 'Employee': {
				switch (this.dragTargetType) {
					case 'MyCalls': this.employee2myCalls(); break
					case 'Employee': this.employee2employee(); break
					case 'QueueMembers': this.employee2queueMember(); break
					case 'QueueCallers': this.employee2queue(); break
				}
				break
			}

			case 'QueueCall': {
				switch (this.dragTargetType) {
					case 'MyCalls': this.queueCall2myCalls(); break
					case 'Employee': this.queueCall2employee(); break
					case 'QueueCallers': this.queueCall2queue(); break
				}
				break
			}

			case 'QueueMember': {
				switch (this.dragTargetType) { // NOSONAR
					case 'QueueMembers': this.queueMember2queueMember(); break
				}
				break
			}

			default:
				break
		}
	}

	// #region Middlemen

	/** A call from my calls has been dropped on an employee */
	private async myCall2Employee() {
		// IN: dragItem=call, dragTarget=employee

		if (this.dragItem && this.dragTarget) {
			const dragItem = this.dragItem as PbxAppEmployeesExtensionCall
			const dragTarget = this.dragTarget as Employee

			// If I have a ringing call, blind transfer it to the employee
			if ([PbxCallState.CALL_START, PbxCallState.CALL_RING].includes(dragItem.state)) {
				logger.info(`CallDrag: User performing blind transfer of own ringing call to employee with extension id ${dragTarget.primaryExtension?._id}`)
				this.blindTransfer(dragItem._id, dragTarget.primaryExtension?._id as string, i18n.t('callDrag.transferToCalledEmployee'))
			} else {
				// Do I have an ongoing call with this Employee, if yes than we do an attendedTransfer, otherwise we do a blindTransfer
				const myCalls = await employees.getMyCalls()
				const myCallsSessionIds = myCalls.map(call => call.sessionId)
				const cc = dragTarget.currentCall
				if (cc && myCallsSessionIds.includes(cc.sessionId)) {
					logger.info('CallDrag: User performing attended transfer of own active call to employee which she is talking to')
					this.attendedTransfer(dragItem._id, cc._id)
				} else {
					logger.info(`CallDrag: User performing blind transfer of own active call to employee with extension id ${dragTarget.primaryExtension?._id}`)
					this.blindTransfer(dragItem._id, dragTarget.primaryExtension?._id as string, i18n.t('callDrag.transferToCalledEmployee'))
				}
			}
		} else {
			logger.error('CallDrag: myCalls2Employee failed: dragItem or dragTarget is undefined')
		}
	}

	/** A call from my calls has been dropped on a queue */
	private myCall2Queue() {
		// IN: dragItem=call, dragTarget=queue

		const dragItem = this.dragItem as PbxAppEmployeesExtensionCall
		const dragTarget = this.dragTarget as PbxAppQueuesAllItem

		logger.info(`CallDrag: User is performing blind transfer of own call to the queue ${dragTarget._id}`)
		this.blindTransfer(dragItem._id, dragTarget._id, i18n.t('callDrag.transferCallToTheQueue'))
	}

	/** A call on an employee hass been dropped on my calls */
	private async employee2myCalls() {
		// IN: dragItem=employee, dragTarget=employee(me)

		const dragItem = this.dragItem as Employee

		await this.hangupMyRingingCallsAndHoldMyActiveCall()
		const cc = dragItem.currentCall
		const cr = dragItem.currentRingingCall
		if (cr) {
			logger.info('CallDrag: User is picking up a ringing call from an employee')
			globalEvents.emit('MyCallsAutoAnswerNextRinging', cr.sessionId)
			api.get(`/pbx/app/action/pickup?device=${ss.realPhoneId}&target=${cr._id}`).catch(_err => {
				globalEvents.emit('CommonErrorModal', { header: i18n.t('callDrag.transferFailedHeader'), message: i18n.t('callDrag.failedToPickupCall') })
				// console.dir(err) // eslint-disable-line no-console
			})
		} else if (cc) {
			logger.info('CallDrag: User is picking up a call from an employee')
			globalEvents.emit('MyCallsAutoAnswerNextRinging', cc.sessionId)
			api.get(`/pbx/app/action/steal?device=${ss.realPhoneId}&target=${cc._id}`).catch(() => {
				globalEvents.emit('CommonErrorModal', { header: i18n.t('callDrag.transferFailedHeader'), message: i18n.t('callDrag.failedToPickupCall') })
			})
		}
	}

	/** Support function for hangup up ringing calls and put active call on hold, before a call dropped in my calls gets answered */
	private async hangupMyRingingCallsAndHoldMyActiveCall() {
		// When dragging a call to me, ensure I do not have another ringing call on me, and if I do - hang it up first
		const myCalls = await employees.getMyCalls()
		if (myCalls.length > 0) {
			for (const call of myCalls) {
				if (call.state === PbxCallState.CALL_RING) {
					if (ss.settings.softphoneId) {
						const session = sipClient.getSession(call.callId, call.caller.number, call.state as unknown as SipClientTcxState)
						if (session) {
							globalEvents.emit('CallRejected', { source: call._id })
							sipClient.decline(session)
							await sleep(200)
						}
					} else {
						try { await api.get(`/pbx/app/action/hangup?target=${call._id}`) }
						catch (_) { /** Ignore */ }
					}
				} else if (call.state === PbxCallState.CALL_ANSWER) {
					if (ss.settings.softphoneId) {
						const session = sipClient.getSession(call.callId, call.caller.number, call.state as unknown as SipClientTcxState)
						if (session) {
							sipClient.hold(session)
							await sleep(200)
						}
					} else {
						try { await api.get(`/pbx/app/action/hold?device=${ss.realPhoneId}&target=${call._id}`) }
						catch (_) { /** Ignore */ }
					}
				}
			}
		}
	}

	/** A call on an employee is dropped on another employee */
	private employee2employee() {
		// IN: dragItem=employee, dragTarget=employee
		const dragItem = this.dragItem as Employee
		const dragTarget = this.dragTarget as Employee
		const cc = dragItem.currentCall

		if (cc) {
			logger.info('CallDrag: User is making a blind transfer from one employee to another employee')
			this.blindTransfer(cc._id, dragTarget.primaryExtension?._id as string, i18n.t('callDrag.transferTheCallToTheEmployee'))
		}
	}

	/** An employee is dropped on a queue (added as queue member) */
	private employee2queueMember() {
		// IN: dragItem=employee, dragTarget=queue
		const dragItem = this.dragItem as Employee
		const dragTarget = this.dragTarget as PbxAppQueuesAllItem

		logger.info(`CallDrag: User is adding employee with id ${dragItem.primaryExtension?._id} to the queue ${dragTarget.name}`)
		this.queueJoinExtension(dragItem.primaryExtension?._id as string, dragTarget)
	}

	/** A call on an employee is dropped on a queue */
	private employee2queue() {
		// IN: dragItem=employee, dragTarget=queue
		const dragItem = this.dragItem as Employee
		const dragTarget = this.dragTarget as PbxAppQueuesAllItem
		const cc = dragItem.currentCall
		if (cc) {
			logger.info(`CallDrag: User is blind transferring a call from employee ${cc._id} to queue ${dragTarget._id}`)
			this.blindTransfer(cc._id, dragTarget._id, i18n.t('callDrag.transferTheCallFromEmployeeToQueue'))
		}
	}

	/** A call in a queue is dropped on my calls*/
	private async queueCall2myCalls() {
		// IN: dragItem=queueCall, dragTarget=employee(me)
		const dragItem = this.dragItem as QueueEnhancedCall

		await this.hangupMyRingingCallsAndHoldMyActiveCall()
		logger.info(`CallDrag: User is answering call ${dragItem.call._id} from a queue`)
		globalEvents.emit('MyCallsAutoAnswerNextRinging', dragItem.call.sessionId)
		api.get(`/pbx/app/action/queuepickup?device=${ss.realPhoneId}&target=${dragItem.call._id}`).catch(() => {
			globalEvents.emit('CommonErrorModal', { header: i18n.t('callDrag.transferFailedHeader'), message: i18n.t('callDrag.failedToPickupQueueCall') })
		})
	}

	/** A call in a queue is dropped on an employee */
	private queueCall2employee() {
		// IN: dragItem=queueCall, dragTarget=employee
		const dragItem = this.dragItem as QueueEnhancedCall
		const dragTarget = this.dragTarget as Employee

		logger.info(`CallDrag: User is transferring queue caller ${dragItem.call._id} to an employee with id ${dragTarget.primaryExtension?._id}`)
		api.get(`/pbx/app/action/queuepickup?extension=${dragTarget.primaryExtension?._id}&target=${dragItem.call._id}`).catch(() => {
			globalEvents.emit('CommonErrorModal', { header: i18n.t('callDrag.transferFailedHeader'), message: i18n.t('callDrag.failedToMoveCallFromQueueToEmployee') })
		})
	}

	/** A call in a queue is dropped on another queue */
	private queueCall2queue() {
		// IN: dragItem=queueCall, dragTarget=queue
		const dragItem = this.dragItem as QueueEnhancedCall
		const dragTarget = this.dragTarget as PbxAppQueuesAllItem

		logger.info(`CallDrag: User is transferring queue caller ${dragItem.call._id} to another queue ${dragTarget._id} (${dragTarget.name})`)
		api.get(`/pbx/app/action/queuepickup?extension=${dragTarget._id}&target=${dragItem.call._id}`).catch(() => {
			globalEvents.emit('CommonErrorModal', { header: i18n.t('callDrag.transferFailedHeader'), message: i18n.t('callDrag.failedToMoveCallFromQueueToQueue') })
		})
	}

	/** A queue member of a queue is dropped on another queue (added as queue member) */
	private queueMember2queueMember() {
		// IN: dragItem=queueMember, dragTarget=queue
		const dragItem = this.dragItem as QueueEnhancedMember
		const dragTarget = this.dragTarget as PbxAppQueuesAllItem

		if (dragItem.member.extension) {
			this.queueJoinExtension(dragItem.member._id, dragTarget)
		} else {
			this.queueJoinExternal(dragItem.member._id, dragTarget)
		}
	}

	// #endregion

	// #region actions

	/**
	 * Perform a blind transfer of a call
	 * @param callId Call ID to transfer
	 * @param extensionId Extension id to transfer to
	 * @param message Message to display in case of error
	 */
	private blindTransfer(callId: string, extensionId: string, message: string) {
		// console.log(`Requested to blind transfer call: ${callId} to extension ${extensionId}`);
		globalEvents.emit('BlindTransferRequested', { source: callId, target: extensionId })
		api.get(`/pbx/app/action/transfer?source=${callId}&target=${extensionId}`).catch(() => {
			globalEvents.emit('CommonErrorModal', { header: i18n.t('callDrag.transferFailedHeader'), message: i18n.t('callDrag.blindTransferError').replace('%%message%%', message) })
		})
	}

	/**
	 * Perform an attended transfer of a call
	 * @param SourceCallId Call ID to transfer
	 * @param destinationCallId Call ID to transfer to
	 */
	private attendedTransfer(SourceCallId: string, destinationCallId: string) {
		// console.log(`Requested to attended transfer call: ${SourceCallId} to ${destinationCallId}`);
		globalEvents.emit('AttendedTransferRequested', { source: SourceCallId, destination: destinationCallId })
		api.get(`/pbx/app/action/transfer?source=${SourceCallId}&destination=${destinationCallId}`).catch(() => {
			globalEvents.emit('CommonErrorModal', { header: i18n.t('callDrag.transferFailedHeader'), message: i18n.t('callDrag.attendedTransferError') })
		})
	}

	/**
	 * Perform a join of an extension to a queue
	 * @param extensionId Id of the extension to add to the queue
	 * @param queue Target queue to add the extension to
	 */
	private queueJoinExtension(extensionId: string, queue: PbxAppQueuesAllItem) {
		// console.log(`Requested to add extension ${extensionId} to queue ${queue._id}`);
		api.post<PbxQueueMemberJoinResponse>(`/pbx/queue/${queue._id}/member`, { extension: extensionId, priority: 1 } as PbxQueueMemberJoinRequest)
			.catch((err: ErrorResponseDetail) => {
				switch (err.status) {
					case 403:
						if (err.code === 'dynamic') { this.showError(i18n.t('callDrag.queueNotDynamic')) }
						else if (err.code === 'extension') { this.showError(i18n.t('callDrag.employeeNotValidForQueue')) }
						else { this.showError(i18n.t('callDrag.queueJoinOtherDenied')) }
						break

					case 404:
						if (err.code === 'id') { this.showError(i18n.t('callDrag.queueNotFound')) }
						else if (err.code === 'extension') { this.showError(i18n.t('callDrag.employeeNotFound')) }
						break

					case 409:
						if (err.code === 'member') { this.showError(i18n.t('callDrag.employeeAlreadyMember')) }
						break

					default:
						this.showError(i18n.t('callDrag.unexpectedError') + ` ${err.status}: ${err.message}`)
						break
				}
			})
	}

	/**
	 * Perform a join of an external number to a queue
	 * @param number Phone number to add to the queue
	 * @param queue Target queue to add the number to
	 */
	private queueJoinExternal(number: string, queue: PbxAppQueuesAllItem) {
		// console.log(`Requested to add external member ${number} to queue ${queue._id}`);
		api.post<PbxQueueMemberJoinResponse>(`/pbx/queue/${queue._id}/member`, { number: number, priority: 1 } as PbxQueueMemberJoinRequest)
			.catch((err: ErrorResponseDetail) => {
				switch (err.status) {
					case 403:
						if (err.code === 'dynamic') { this.showError(i18n.t('callDrag.queueNotDynamic')) }
						else { this.showError(i18n.t('callDrag.noPermissionToAddExternalToQueue')) }
						break

					case 404:
						if (err.code === 'id') { this.showError(i18n.t('callDrag.queueNotFound')) }
						break

					case 409:
						if (err.code === 'member') { this.showError(i18n.t('callDrag.externalAlreadyMember')) }
						else if (err.code === 'extension') { this.showError(i18n.t('callDrag.noPermissionToAddExternalToQueue')) }
						break

					default:
						this.showError(i18n.t('callDrag.unexpectedError') + ` ${err.status}: ${err.message}`)
						break
				}
			})
	}

	/**
	 * Common error message handler
	 * @param message Error message to show
	 * @param header Optional header for the error message, defaults to 'Add new member'
	 */
	private showError(message: string, header?: string) {
		globalEvents.emit('CommonErrorModal', { header: header || i18n.t('callDrag.addNewMemberHeader'), message: message })
	}

	// #endregion
}
